import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Button, Spin, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { H5_TAB_BAR_PX } from '../h5Layout';

export default function Return() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchRecords = () => {
    setLoading(true);
    api.get('/borrows/mine', { params: { status: 1 } })
      .then(res => setRecords(Array.isArray(res) ? res : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRecords(); }, []);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    if (selected.length === records.length) {
      setSelected([]);
    } else {
      setSelected(records.map(r => r.id));
    }
  };

  const handleReturn = async () => {
    if (selected.length === 0) { message.warning('请选择要归还的资产'); return; }
    setSubmitting(true);
    try {
      await api.post('/borrows/return', { record_ids: selected });
      message.success('归还成功');
      setSelected([]);
      fetchRecords();
    } catch (err) {
      message.error(err.message || '归还失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      paddingBottom: `calc(${H5_TAB_BAR_PX}px + 88px + env(safe-area-inset-bottom, 0px))`,
    }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600 }}>归还资产</span>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', marginBottom: 8 }}>
        <div style={{ fontSize: 14, color: '#666' }}>
          <span style={{ fontWeight: 600, color: '#333', fontSize: 18 }}>{records.length}</span>
          <span>件</span>
          <span style={{ margin: '0 8px', color: '#ddd' }}>|</span>
          <span>已选择 </span>
          <span style={{ fontWeight: 600, color: '#52c41a', fontSize: 18 }}>{selected.length}</span>
          <span>件</span>
        </div>
        {records.length > 0 && (
          <Checkbox
            checked={selected.length === records.length && records.length > 0}
            onChange={selectAll}
            style={{ fontSize: 14 }}
          >
            全选
          </Checkbox>
        )}
      </div>

      {/* Asset cards */}
      <div style={{ padding: '0 16px' }}>
        <Spin spinning={loading}>
          {records.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>暂无借用中的资产</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {records.map(r => {
                const isSelected = selected.includes(r.id);
                return (
                  <div
                    key={r.id}
                    onClick={() => toggleSelect(r.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: isSelected ? '#f0fff4' : '#fff',
                      borderRadius: 8, padding: '14px 12px',
                      borderLeft: isSelected ? '4px solid #52c41a' : '4px solid transparent',
                      border: isSelected ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                      borderLeftWidth: 4,
                      borderLeftColor: isSelected ? '#52c41a' : 'transparent',
                    }}
                  >
                    <Checkbox checked={isSelected} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 500, color: '#333' }}>{r.asset_name}</span>
                        <span style={{ fontSize: 12, color: '#999' }}>{r.asset_no}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#999' }}>
                        <span>借用日期: {dayjs(r.borrow_time || r.created_at).format('YYYY-MM-DD')}</span>
                        {r.expected_return_time && (
                          <span>预计归还: {dayjs(r.expected_return_time).format('YYYY-MM-DD')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Spin>
      </div>

      {/* Bottom button */}
      {records.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: H5_TAB_BAR_PX,
          left: 0,
          right: 0,
          padding: '12px 16px',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          zIndex: 90,
        }}
        >
          <Button
            type="primary"
            block
            size="large"
            loading={submitting}
            onClick={handleReturn}
            disabled={selected.length === 0}
            style={{ borderRadius: 8, height: 46, fontSize: 16, fontWeight: 500, background: '#52c41a', borderColor: '#52c41a' }}
          >
            一键归还已选资产
          </Button>
        </div>
      )}
    </div>
  );
}
